import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn, focusRing } from "./utils";

export type ChipProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  pressed?: boolean;
  size?: "sm" | "md";
  /** Gold count shown inside the chip, such as the number of unread chats. */
  count?: number;
  leading?: ReactNode;
  children: ReactNode;
};

export function Chip({
  pressed = false,
  size = "md",
  count,
  leading,
  className,
  children,
  ...rest
}: ChipProps) {
  // A count of nothing is left out, so a gold zero never shows.
  const shown = count !== undefined && count > 0 ? count : undefined;
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        "inline-flex flex-none items-center gap-1.5 rounded-full cursor-pointer whitespace-nowrap transition-colors",
        size === "md" ? "h-[38px] text-sm" : "h-8 text-[13px]",
        shown !== undefined && size === "md" ? "pl-4 pr-3" : size === "md" ? "px-4" : "px-3",
        pressed
          ? "bg-blue font-semibold text-white"
          : "border border-line bg-surface font-medium text-ink hover:bg-linen",
        focusRing,
        className,
      )}
      {...rest}
    >
      {leading}
      {children}
      {shown !== undefined &&
        (size === "md" ? (
          <span className="rounded-full bg-gold px-[7px] py-px text-xs font-bold text-ink">{shown}</span>
        ) : (
          <span>{shown}</span>
        ))}
    </button>
  );
}

export type FilterOption<T extends string> = { id: T; label: string; count?: number };

export function FilterTabs<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  label = "Filter",
  className,
}: {
  options: FilterOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  label?: string;
  className?: string;
}) {
  return (
    <fieldset aria-label={label} className={cn("m-0 flex min-w-0 gap-2 border-0 p-0", className)}>
      {options.map((o) => (
        <Chip key={o.id} pressed={o.id === value} size={size} count={o.count} onClick={() => onChange(o.id)}>
          {o.label}
        </Chip>
      ))}
    </fieldset>
  );
}

// Two or three options on a sunken track, such as "On this phone" and "Penielly".
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
  className,
}: {
  options: { id: T; label: ReactNode }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  className?: string;
}) {
  return (
    <fieldset
      aria-label={label}
      className={cn("m-0 flex gap-1 rounded-field border-0 bg-surface-header p-1", className)}
    >
      {options.map((o) => {
        const on = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(o.id)}
            className={cn(
              "flex h-9 flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-[9px] text-sm font-semibold",
              on ? "bg-surface text-ink" : "bg-transparent text-ink-secondary",
              focusRing,
            )}
          >
            {o.label}
          </button>
        );
      })}
    </fieldset>
  );
}
